import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { PanelChevronComponent } from 'src/app/UI-Lib/Weissman-Panel/panel.chevron.component';
import { ReferralsService } from '../referrals.service';
import { AnchorRouteDirective } from 'src/app/UI-Lib/Directives';
import { ToastrService } from 'ngx-toastr';
import { filter, map, partition, some, uniq } from 'lodash';
import { TaskService } from 'src/app/Task/task.service.upgrade';
import { LaunchTaskModalService } from 'src/app/Task/launchTaskModal.service';
import { WeissmanDatetimeComponent } from 'src/app/UI-Lib/Weiss-Datepicker/weissman-datetime.component';
import { EntityTypeIds } from 'src/app/constants.new';
import { MessageModalService } from 'src/app/UI-Lib/Message-Box/messageModal.service';
import { BusyIndicatorService } from 'src/app/Busy-Indicator/busyIndicator.service';
import { AceLoaderComponent } from '../../../UI-Lib/ACE/Loader/loader.component';

@Component({
    selector: 'referrals-panel',
    templateUrl: 'referralsPanel.component.html',
    standalone: true,
    imports: [AceLoaderComponent, CommonModule, PanelChevronComponent, AnchorRouteDirective, WeissmanDatetimeComponent],
})
export class ReferralsPanelComponent implements OnInit {
    constructor(
        private readonly _referralsService: ReferralsService,
        private readonly _messageModalService: MessageModalService,
        private readonly _toastr: ToastrService,
        private readonly _busyService: BusyIndicatorService,
        private readonly _taskService: TaskService,
        private readonly _launchTaskModalService: LaunchTaskModalService,
    ) {}

    @Input() companyId: number;
    @Input() siteId: number;

    showBody: boolean = false;
    hasViewPermission: boolean = true;
    isLoading: boolean = false;
    eligibleAppealCount: number = 0;
    hoveredReferralId?: number;

    referrals: Core.LitigationReferralListModel[] = [];

    ngOnInit() {}

    expandOrCollapse(): void {
        this.showBody = !this.showBody;

        if (this.showBody) {
            this.loadPanel();
        }
    }

    async loadPanel(): Promise<void> {
        this.isLoading = true;

        try {
            if (this.companyId) {
                this.referrals = await this._referralsService.getCompanyLitigationReferrals(this.companyId);
            } else if (this.siteId) {
                this.referrals = await this._referralsService.getSiteLitigationReferrals(this.siteId);
                this.eligibleAppealCount = await this._referralsService.getShowCreateReferralButton(this.siteId);
            }
        } finally {
            this.isLoading = false;
        }
    }

    async createReferral(): Promise<void> {
        try {
            await this._messageModalService.confirm(
                `You are about to create a Litigation Referral for ${this.eligibleAppealCount} appeal(s). Do you want to continue?`,
                'Confirm Create',
            );
        } catch {
            return;
        }

        this.isLoading = true;

        try {
            const createResult = await this._referralsService.createLitigationReferralBySite(this.siteId);
            this._handleReferralCreate(createResult);

            await this.loadPanel();
        } finally {
            this.isLoading = false;
        }
    }

    launchTaskModal(event, referralId: number): void {
        event.preventDefault();
        this._launchTaskModalService.launchTaskModal(referralId, EntityTypeIds.LITIGATION_REFERRAL, true);
    }

    async deleteReferral(referralId: number): Promise<void> {
        try {
            const matterId = this.referrals.find((x) => x.litigationReferralId === referralId)?.matterId;
            await this._messageModalService.confirm(
                `Are you sure you want to remove Litigation Referral "${matterId}"?`,
                'Confirm Delete?',
            );
        } catch (e) {
            return;
        }

        const busyRef = this._busyService.show({ message: 'Deleting' });

        try {
            await this._referralsService.deleteLitigationReferral(referralId);
            await this.loadPanel();
        } finally {
            busyRef.hide();
        }
    }

    private _handleReferralCreate(result: Core.LitigationReferralCreateResult): void {
        const [successfulResults, errorResults] = partition(
            result.operationResult,
            (x) => !x.errorMessage && !x.warningMessage,
        );

        if (errorResults.length < 1) {
            this._toastr.success(
                `Successfully created ${result.numReferralsCreated} litigation referrals from ${result.numAppealGroups} appeal groupings for ${result.numEligibleAppeals} eligible appeals!`,
            );
        } else {
            const warnings: string[] = uniq(
                map(
                    filter(errorResults, function (error) {
                        return !!error.warningMessage;
                    }),
                    'warningMessage',
                ),
            );

            const errors: string[] = uniq(
                map(
                    filter(errorResults, function (error) {
                        return !error.warningMessage;
                    }),
                    'errorMessage',
                ),
            );

            if (some(successfulResults)) {
                this._toastr.warning(
                    `Only some litigation referral(s) successfully created; from ${result.numAppealGroups} appeal groupings for ${result.numEligibleAppeals} eligible appeals, ${result.numReferralsCreated} succeeded and ${errorResults.length} failed`,
                );
            }

            if (some(errors) || some(warnings)) {
                this._taskService.showErrorNotificationModal(
                    errors,
                    warnings,
                    'The following error(s) were encountered attempting to create litigation referral(s):',
                );
            }
        }
    }
}
